import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

import { getApiBaseUrl } from '@/lib/apiBase';

const API = getApiBaseUrl();

export default function ProductEditDialog({ product, open, onClose, token, onSaved }) {
  const [form, setForm] = useState({
    name: '',
    brand: '',
    articul: '',
    oem: '',
    category: '',
    description: '',
    packing_rate: '1',
    unit: 'шт',
    ntin: '',
    barcodes: '',
    client_price: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!product) return;
    setForm({
      name: product.name || '',
      brand: product.brand || '',
      articul: product.articul || '',
      oem: product.oem || '',
      category: product.category || '',
      description: product.description || '',
      packing_rate: String(product.packing_rate || 1),
      unit: product.unit || 'шт',
      ntin: product.ntin || '',
      barcodes: Array.isArray(product.barcodes) ? product.barcodes.join(', ') : '',
      client_price:
        product.client_price == null || product.client_price === ''
          ? ''
          : String(Math.round(Number(product.client_price) || 0)),
    });
  }, [product]);

  const setField = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    if (!product || !token) return;
    const packingRate = parseInt(form.packing_rate, 10);
    const clientPrice = Number(String(form.client_price).replace(/\s/g, '').replace(',', '.'));
    if (!form.name.trim()) {
      toast.error('Укажите название товара');
      return;
    }
    if (!Number.isFinite(packingRate) || packingRate < 1) {
      toast.error('Укажите корректную упаковку');
      return;
    }
    if (!Number.isFinite(clientPrice) || clientPrice < 0) {
      toast.error('Укажите корректную цену');
      return;
    }

    setSaving(true);
    try {
      const barcodes = form.barcodes
        .split(/[,;\n]+/)
        .map((b) => b.trim())
        .filter(Boolean);

      const { data } = await axios.patch(
        `${API}/products/${product.id}`,
        {
          name: form.name.trim(),
          brand: form.brand.trim(),
          articul: form.articul.trim(),
          oem: form.oem.trim(),
          category: form.category.trim(),
          description: form.description.trim(),
          packing_rate: packingRate,
          unit: form.unit.trim() || 'шт',
          ntin: form.ntin.trim(),
          barcodes,
          client_price: clientPrice,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Карточка товара сохранена');
      onSaved?.(data);
      onClose();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail ? `Ошибка: ${detail}` : 'Не удалось сохранить карточку');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Редактирование карточки</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div className="sm:col-span-2 space-y-1.5">
            <Label htmlFor="edit-name">Название</Label>
            <Input id="edit-name" value={form.name} onChange={setField('name')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-brand">Бренд</Label>
            <Input id="edit-brand" value={form.brand} onChange={setField('brand')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-category">Категория</Label>
            <Input id="edit-category" value={form.category} onChange={setField('category')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-articul">Артикул</Label>
            <Input id="edit-articul" value={form.articul} onChange={setField('articul')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-oem">OEM</Label>
            <Input id="edit-oem" value={form.oem} onChange={setField('oem')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-packing">Упаковка</Label>
            <Input
              id="edit-packing"
              type="number"
              min="1"
              value={form.packing_rate}
              onChange={setField('packing_rate')}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-client-price">Цена, ₸</Label>
            <Input
              id="edit-client-price"
              type="number"
              min="0"
              step="1"
              value={form.client_price}
              onChange={setField('client_price')}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-unit">Единица</Label>
            <Input id="edit-unit" value={form.unit} onChange={setField('unit')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-ntin">NTIN</Label>
            <Input id="edit-ntin" value={form.ntin} onChange={setField('ntin')} />
          </div>
          <div className="sm:col-span-2 space-y-1.5">
            <Label htmlFor="edit-barcodes">Штрихкоды</Label>
            <Input
              id="edit-barcodes"
              value={form.barcodes}
              onChange={setField('barcodes')}
              placeholder="Через запятую"
            />
          </div>
          <div className="sm:col-span-2 space-y-1.5">
            <Label htmlFor="edit-description">Описание</Label>
            <Textarea
              id="edit-description"
              value={form.description}
              onChange={setField('description')}
              rows={4}
            />
          </div>
          {product?.code && (
            <div className="sm:col-span-2 text-xs text-gray-500">
              Код товара: <span className="font-mono">{product.code}</span> (не редактируется)
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
